import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BBALoader from '../../components/BBALoader';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, Lock, MessageSquare, Pencil, Send, TrendingUp, Trash2 } from 'lucide-react';
import { arrayUnion, collection, deleteDoc, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import BackButton from '../../components/BackButton';
import Modal from '../../components/Modal';
import AlaCarteCatalogStructureEditor from '../../components/crm/AlaCarteCatalogStructureEditor';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getUserData, CRM_OWNER_EXECUTIVE_ROLES } from '../../services/accessControlService';
import {
  normalizeAlaCarteItem,
  PRICING_STATUS,
  CATALOG_STATUS_LABELS,
  CATALOG_STATUS_OPTIONS,
  computePricingMetrics,
} from '../../utils/alaCarteCatalog';
import { buildAlaCarteUpdatePayload, stripIdForFirestore, toCatalogEditDraft } from '../../utils/alaCarteCatalogDraft';
import PortalBackground from '../../components/PortalBackground';

// Lazy-loaded so the full emoji picker bundle only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const DEPARTMENT_OPTIONS = ['General', 'Sales', 'Operations', 'Finance', 'Technology', 'Marketing', 'HR'];

const OVERVIEW_TABS = [
  { id: 'overview', label: 'Overview', Icon: FileText },
  { id: 'notes', label: 'Notes', Icon: MessageSquare },
  { id: 'financial', label: 'Financial', Icon: TrendingUp },
];

/** Note `at` may be a Firestore Timestamp, a Date, or an ISO string. */
function noteTimeMs(v) {
  if (!v) return 0;
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatNoteTime(v) {
  const ms = noteTimeMs(v);
  return ms ? new Date(ms).toLocaleString() : '';
}

function nameInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

const NoteAvatar = ({ name, photo, size = 'h-9 w-9' }) =>
  photo ? (
    <img src={photo} alt={name} className={`${size} shrink-0 rounded-full object-cover`} />
  ) : (
    <span
      className={`${size} flex shrink-0 items-center justify-center rounded-full bg-red-100 text-sm font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300`}
    >
      {nameInitials(name)}
    </span>
  );

// The status picker offers the four canonical statuses. Unpromoted keeps the
// item on file (existing client deliverables untouched) but hides it from
// every purchase surface.
const STATUS_LABELS = CATALOG_STATUS_LABELS;
const STATUS_OPTIONS = CATALOG_STATUS_OPTIONS;

const STATUS_BADGE_CLASSES =
  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';

function parseMoney(raw) {
  if (raw === '' || raw === undefined || raw === null) return NaN;
  const n = parseFloat(String(raw));
  return Number.isFinite(n) ? n : NaN;
}

function formatMoney(n) {
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function catalogSaveChangeLabels(prevItem, nextPayload) {
  const labels = [];
  const keys = ['name', 'description', 'pricingType', 'department', 'popularAddOn', 'alwaysRequired', 'parentItemId', 'pricingStatus', 'internalCost', 'clientListPrice', 'minimumTermMonths', 'caseByCaseMultiplier'];
  for (const k of keys) {
    if (prevItem[k] !== nextPayload[k]) labels.push(k);
  }
  try {
    if (JSON.stringify(prevItem.standardPricing || {}) !== JSON.stringify(nextPayload.standardPricing || {})) {
      labels.push('standardPricing');
    }
    if (JSON.stringify(prevItem.packages || []) !== JSON.stringify(nextPayload.packages || [])) {
      labels.push('packages');
    }
    if (JSON.stringify(prevItem.deliverable || {}) !== JSON.stringify(nextPayload.deliverable || {})) {
      labels.push('deliverable');
    }
    if (JSON.stringify(prevItem.exclusiveChoiceItemIds || []) !== JSON.stringify(nextPayload.exclusiveChoiceItemIds || [])) {
      labels.push('exclusiveChoiceItemIds');
    }
  } catch {
    labels.push('catalog');
  }
  return labels;
}

export default function AlaCarteItemOverview() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isExec, setIsExec] = useState(false);
  const [item, setItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [noteDraft, setNoteDraft] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  const [userProfile, setUserProfile] = useState({ name: '', photo: '' });
  // byUid → { name, photo } resolved from internal_users so old notes show real authors.
  const [noteAuthors, setNoteAuthors] = useState({});

  const [catalogDraft, setCatalogDraft] = useState(null);
  const [allCatalogItems, setAllCatalogItems] = useState([]);
  const [parentSearch, setParentSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const itemsSnap = await getDocs(collection(db, 'alaCarteItems'));
        if (cancelled) return;
        setAllCatalogItems(itemsSnap.docs.map((d) => normalizeAlaCarteItem(d.id, d.data())));
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    try {
      const userData = await getUserData(currentUser?.uid);
      const exec = !!userData && CRM_OWNER_EXECUTIVE_ROLES.includes(userData.role);
      setIsExec(exec);
      // getUserData wraps the Firestore doc under `.data` (internal_users fields:
      // firstName/lastName/profileImage).
      const profile = userData?.data || {};
      setUserProfile({
        name:
          [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
          profile.name ||
          profile.email ||
          '',
        photo: profile.profileImage || '',
      });

      const ref = doc(db, 'alaCarteItems', itemId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setError('Item not found');
        setItem(null);
        return;
      }
      if (!exec) {
        setItem(null);
        setError('');
        return;
      }
      const normalized = normalizeAlaCarteItem(snap.id, snap.data());
      setItem(normalized);
      setCatalogDraft(toCatalogEditDraft(normalized));
      setParentSearch('');
    } catch (e) {
      console.error(e);
      setError('Failed to load item');
    } finally {
      setLoading(false);
    }
  }, [itemId, currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const pushChangeLog = async (ref, prevItem, changeLabels) => {
    const nextVersion = (prevItem.catalogVersion || 0) + 1;
    const entry = {
      version: nextVersion,
      at: new Date(),
      byUid: currentUser?.uid || '',
      changes: changeLabels,
    };
    const prevLog = Array.isArray(prevItem.catalogChangeLog) ? prevItem.catalogChangeLog : [];
    const catalogChangeLog = [...prevLog, entry].slice(-100);
    await updateDoc(ref, {
      catalogVersion: nextVersion,
      catalogChangeLog,
      updatedAt: new Date(),
    });
  };

  const saveAllChanges = async () => {
    if (!item || !isExec || !catalogDraft) return;
    const built = buildAlaCarteUpdatePayload(catalogDraft);
    if (!built.ok) {
      alert(built.message);
      return;
    }
    setSaving(true);
    try {
      const catalogPayload = stripIdForFirestore(built.data);
      // Notes post immediately from the Notes tab via arrayUnion — never write the feed here.
      delete catalogPayload.internalNoteMessages;
      let changeLabels = catalogSaveChangeLabels(item, catalogPayload);
      if (changeLabels.length === 0) changeLabels = ['catalog'];
      const ref = doc(db, 'alaCarteItems', item.id);
      await updateDoc(ref, {
        ...catalogPayload,
        updatedAt: new Date(),
      });
      await pushChangeLog(ref, item, changeLabels);
      await load();
      setEditing(false);
    } catch (e) {
      console.error(e);
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Discard in-flight edits: restore the draft from the saved item.
  const cancelEdit = () => {
    if (item) setCatalogDraft(toCatalogEditDraft(item));
    setParentSearch('');
    setEditing(false);
  };

  // Notes tab: append a message to the item's internal feed immediately
  // (no edit mode required — notes are CRM-only chatter, not catalog data).
  const postNote = async () => {
    const text = noteDraft.trim();
    if (!item || !isExec || !text) return;
    setPostingNote(true);
    try {
      const entry = {
        text,
        byUid: currentUser?.uid || '',
        byName: userProfile.name || 'Internal user',
        byPhoto: userProfile.photo || '',
        at: new Date(),
      };
      await updateDoc(doc(db, 'alaCarteItems', item.id), {
        internalNoteMessages: arrayUnion(entry),
        updatedAt: new Date(),
      });
      setItem((prev) =>
        prev
          ? {
              ...prev,
              internalNoteMessages: [
                ...(Array.isArray(prev.internalNoteMessages) ? prev.internalNoteMessages : []),
                entry,
              ],
            }
          : prev
      );
      setNoteDraft('');
    } catch (e) {
      console.error(e);
      alert('Failed to post note');
    } finally {
      setPostingNote(false);
    }
  };

  // Authors can delete their own notes. noteMessages sorts a shallow copy, so
  // entries share references with item.internalNoteMessages — filter by identity.
  const deleteNote = async (note) => {
    if (!item || !currentUser?.uid || note?.byUid !== currentUser.uid) return;
    if (!window.confirm('Delete this note?')) return;
    const remaining = (Array.isArray(item.internalNoteMessages) ? item.internalNoteMessages : []).filter(
      (n) => n !== note
    );
    try {
      await updateDoc(doc(db, 'alaCarteItems', item.id), {
        internalNoteMessages: remaining,
        updatedAt: new Date(),
      });
      setItem((prev) => (prev ? { ...prev, internalNoteMessages: remaining } : prev));
    } catch (e) {
      console.error(e);
      alert('Failed to delete note');
    }
  };

  const handleDeleteCatalogItem = async () => {
    if (!item || !isExec) return;
    if (!window.confirm('Delete this catalog item? This cannot be undone.')) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, 'alaCarteItems', item.id));
      navigate('/crm/alacartetemplate');
    } catch (e) {
      console.error(e);
      alert('Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!item || !isExec) return;
    const internal =
      item.internalCost === '' || item.internalCost === undefined
        ? NaN
        : parseFloat(String(item.internalCost));
    const clientRaw =
      item.clientListPrice === '' || item.clientListPrice === undefined
        ? item.standardPricing?.cost != null
          ? String(item.standardPricing.cost)
          : ''
        : String(item.clientListPrice);
    const client = parseFloat(clientRaw);
    if (!Number.isFinite(client) || client < 0) {
      alert('Set a valid client list price or standard cost in catalog structure before publishing.');
      return;
    }
    const pricingTypesWithStandard = ['standard', 'term', 'alwaysIncluded'];
    if (pricingTypesWithStandard.includes(item.pricingType)) {
      if (!Number.isFinite(internal) || internal < 0) {
        alert('Set internal cost in catalog structure before publishing standard, term, or always-included items.');
        return;
      }
    }

    if (!window.confirm('Publish to client catalog? Team draft will copy into client scope and deliverables where applicable.')) return;

    setSaving(true);
    try {
      const teamScope = (item.teamInput && item.teamInput.scope) || '';
      const teamRows = Array.isArray(item.teamInput?.deliverableRows) ? item.teamInput.deliverableRows : [];
      const existingRows = Array.isArray(item.deliverableRows) ? item.deliverableRows : [];
      const rowsMeaningful = (rows) =>
        rows.some((r) => String(r?.quantity || '').trim() || String(r?.description || '').trim());
      const existingScope = typeof item.scope === 'string' ? item.scope.trim() : '';
      const nextScope = teamScope.trim() || item.description || existingScope || '';
      const nextRows = rowsMeaningful(existingRows)
        ? existingRows
        : teamRows.length > 0
          ? teamRows
          : existingRows;

      const payload = {
        pricingStatus: PRICING_STATUS.published,
        scope: nextScope,
        deliverableRows: nextRows,
        internalCost:
          item.internalCost === '' || item.internalCost === undefined
            ? ''
            : parseFloat(String(item.internalCost)),
        clientListPrice: client,
        updatedAt: new Date(),
      };

      if (pricingTypesWithStandard.includes(item.pricingType)) {
        payload.standardPricing = {
          ...(item.standardPricing || {}),
          cost: client,
          unit: item.standardPricing?.unit || 'item',
          stripeProductId: item.standardPricing?.stripeProductId || '',
        };
      }

      const ref = doc(db, 'alaCarteItems', item.id);
      await updateDoc(ref, payload);
      await pushChangeLog(ref, item, ['Published to client catalog']);
      await load();
    } catch (e) {
      console.error(e);
      alert('Publish failed');
    } finally {
      setSaving(false);
    }
  };

  // Live financial numbers — read from the draft so edits recompute immediately.
  const pricingSummary = useMemo(() => {
    if (!item || !catalogDraft) {
      return { internalNum: NaN, clientNum: NaN, profit: NaN, marginPercent: null };
    }
    const internalNum = parseMoney(catalogDraft.internalCost ?? item.internalCost);
    let clientNum = NaN;
    if (catalogDraft.clientListPrice !== '' && catalogDraft.clientListPrice !== undefined) {
      clientNum = parseMoney(catalogDraft.clientListPrice);
    }
    if (!Number.isFinite(clientNum) && catalogDraft.standardPricing?.cost != null) {
      clientNum = parseMoney(catalogDraft.standardPricing.cost);
    }
    const profit =
      Number.isFinite(internalNum) && Number.isFinite(clientNum) ? clientNum - internalNum : NaN;
    const { marginPercent } = computePricingMetrics(internalNum, clientNum);
    return { internalNum, clientNum, profit, marginPercent };
  }, [item, catalogDraft]);

  // Financial tab only applies to standard (and legacy term) pricing — bounce
  // back to Overview if the pricing type changes out from under it.
  useEffect(() => {
    const t = catalogDraft?.pricingType || item?.pricingType;
    if (activeTab === 'financial' && ['child', 'caseByCase', 'alwaysIncluded'].includes(t)) {
      setActiveTab('overview');
    }
  }, [activeTab, catalogDraft?.pricingType, item?.pricingType]);

  // Newest first for the Notes tab feed.
  const noteMessages = useMemo(() => {
    const arr = Array.isArray(item?.internalNoteMessages) ? [...item.internalNoteMessages] : [];
    return arr.sort((a, b) => noteTimeMs(b?.at) - noteTimeMs(a?.at));
  }, [item]);

  // Resolve note authors from internal_users so names/photos stay current even
  // for notes saved before byName/byPhoto were denormalized onto entries.
  useEffect(() => {
    const uids = [...new Set(noteMessages.map((n) => n?.byUid).filter(Boolean))].filter(
      (uid) => !(uid in noteAuthors)
    );
    if (uids.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        uids.map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, 'internal_users', uid));
            if (snap.exists()) {
              const d = snap.data();
              return [
                uid,
                {
                  name: [d.firstName, d.lastName].filter(Boolean).join(' ') || d.name || d.email || '',
                  photo: d.profileImage || '',
                },
              ];
            }
          } catch (e) {
            console.error('Error resolving note author:', e);
          }
          return [uid, null];
        })
      );
      if (!cancelled) setNoteAuthors((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => {
      cancelled = true;
    };
  }, [noteMessages, noteAuthors]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24 relative">
        <PortalBackground />
        <div className="relative"><BBALoader size={48} /></div>
      </div>
    );
  }

  if (!loading && !isExec) {
    return (
      <div className="min-h-screen px-4 py-12 relative">
        <PortalBackground />
        <div className="relative">
        <BackButton to="/crm/alacartetemplate" />
        <p className="mt-6 text-gray-600 dark:text-gray-400">Owner or Executive access is required for this overview.</p>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen px-4 py-12 relative">
        <PortalBackground />
        <div className="relative">
        <BackButton to="/crm/alacartetemplate" />
        <p className="mt-6 text-gray-600 dark:text-gray-400">{error || 'Not found'}</p>
        </div>
      </div>
    );
  }

  const draftPricingType = catalogDraft?.pricingType || item.pricingType;
  const showFinancialTab =
    draftPricingType !== 'child' && draftPricingType !== 'caseByCase' && draftPricingType !== 'alwaysIncluded';
  const canPublishCatalog =
    draftPricingType !== 'caseByCase' && draftPricingType !== 'child';

  return (
    <div className="min-h-screen px-4 py-10 relative">
      <PortalBackground />
      <div className="relative mx-auto max-w-5xl">
        <header className="relative mb-8">
          <div className="absolute left-0 top-0 z-10">
            <BackButton to="/crm/alacartetemplate" className="shrink-0" />
          </div>

          {/* Edit (view mode) / Delete · Cancel · Save (edit mode) */}
          <div className="absolute right-0 top-0 z-10 flex items-center gap-2">
            {!editing ? (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </button>
            ) : (
              <>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleDeleteCatalogItem}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={cancelEdit}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={saving || !catalogDraft}
                  onClick={saveAllChanges}
                  className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-900 disabled:opacity-50 dark:bg-gray-600 dark:hover:bg-gray-500"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>

          {/* Logo + name at the top */}
          <div className="mx-auto max-w-2xl px-24 text-center sm:px-28">
            {editing ? (
              <button
                type="button"
                onClick={() => setShowEmojiPicker(true)}
                title="Change icon"
                className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full border-2 border-gray-300 bg-white text-3xl transition-colors hover:border-red-500 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-red-400"
              >
                {catalogDraft?.icon || item.icon}
              </button>
            ) : (
              <div className="mb-3 text-5xl leading-none">{item.icon}</div>
            )}

            {editing ? (
              <input
                type="text"
                value={catalogDraft?.name || ''}
                onChange={(e) => setCatalogDraft((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Item name"
                className="mx-auto block w-full max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-2xl font-bold text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            ) : (
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white md:text-3xl">{item.name}</h1>
            )}

          </div>
        </header>

        {/* Tabs */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex items-center gap-1 rounded-2xl border border-gray-200 bg-white p-1 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            {OVERVIEW_TABS.filter(({ id }) => id !== 'financial' || showFinancialTab).map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === id
                    ? 'bg-red-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'overview' && (
        <div className="space-y-8">
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            {/* Status · Department · Popular Add On — one row above the description */}
            <div className="mb-6 flex flex-wrap items-center gap-2">
              {editing ? (
                <select
                  value={catalogDraft?.pricingStatus || PRICING_STATUS.draft}
                  onChange={(e) => setCatalogDraft((prev) => ({ ...prev, pricingStatus: e.target.value }))}
                  className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {/* Keep a legacy team-workflow status selectable so opening the
                      picker doesn't silently rewrite it. */}
                  {(STATUS_OPTIONS.includes(catalogDraft?.pricingStatus) || !catalogDraft?.pricingStatus
                    ? STATUS_OPTIONS
                    : [catalogDraft.pricingStatus, ...STATUS_OPTIONS]
                  ).map((value) => (
                    <option key={value} value={value}>
                      {STATUS_LABELS[value] || value}
                    </option>
                  ))}
                </select>
              ) : (
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_BADGE_CLASSES}`}>
                  {STATUS_LABELS[item.pricingStatus] || item.pricingStatus}
                </span>
              )}

              {editing ? (
                <select
                  value={catalogDraft?.department || 'General'}
                  onChange={(e) => setCatalogDraft((prev) => ({ ...prev, department: e.target.value }))}
                  className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {DEPARTMENT_OPTIONS.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 dark:border-gray-600 dark:text-gray-300">
                  {item.department || 'General'}
                </span>
              )}

              {editing ? (
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 dark:border-gray-600 dark:bg-gray-800">
                  <input
                    type="checkbox"
                    checked={catalogDraft?.popularAddOn === true}
                    onChange={(e) => setCatalogDraft((prev) => ({ ...prev, popularAddOn: e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Popular Add On</span>
                </label>
              ) : (
                item.popularAddOn === true && (
                  <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    ⭐ Popular Add On
                  </span>
                )
              )}

              {/* Always required: auto-selected in every program, can't be removed, quantity pinned to 1. */}
              {editing ? (
                <button
                  type="button"
                  aria-pressed={catalogDraft?.alwaysRequired === true}
                  onClick={() =>
                    setCatalogDraft((prev) => {
                      const on = prev?.alwaysRequired !== true;
                      return on
                        ? {
                            ...prev,
                            alwaysRequired: true,
                            hasDeliverable: true,
                            deliverable: { ...(prev.deliverable || {}), quantity: '1', maxQuantity: '1' },
                          }
                        : { ...prev, alwaysRequired: false };
                    })
                  }
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    catalogDraft?.alwaysRequired === true
                      ? 'border-red-600 bg-red-600 text-white hover:bg-red-700'
                      : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <Lock className="h-3.5 w-3.5" />
                  {catalogDraft?.alwaysRequired === true ? 'Always Required · Qty 1' : 'Make Always Required'}
                </button>
              ) : (
                item.alwaysRequired === true && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    <Lock className="h-3 w-3" />
                    Always Required · Qty 1
                  </span>
                )
              )}

              {item.postedToTeam && (
                <span className="text-xs text-gray-500 dark:text-gray-400">Posted to: {item.postedToTeam}</span>
              )}
            </div>

            {catalogDraft && (
              <AlaCarteCatalogStructureEditor
                draft={catalogDraft}
                setDraft={setCatalogDraft}
                catalogItems={allCatalogItems}
                parentSearch={parentSearch}
                setParentSearch={setParentSearch}
                disabled={!editing}
              />
            )}
          </section>

          {draftPricingType === 'caseByCase' && (
            <section className="rounded-lg border border-blue-200 bg-blue-50/80 p-6 dark:border-blue-800 dark:bg-blue-900/20">
              <h2 className="mb-2 text-lg font-semibold text-blue-900 dark:text-blue-100">Case-by-case pricing</h2>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This service template uses per-account bids. Request bids from a lead or client overview, or they
                are created automatically when a portal user adds this item to their cart.
              </p>
            </section>
          )}

          {canPublishCatalog && (
            <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Publish to client catalog</h2>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Make this fixed-price service visible in the portal à la carte shop.
              </p>
              <button
                type="button"
                disabled={saving}
                onClick={handlePublish}
                className="rounded-lg bg-green-700 px-4 py-2 text-white hover:bg-green-800 disabled:opacity-50"
              >
                Publish client catalog
              </button>
            </section>
          )}
        </div>
        )}

        {activeTab === 'notes' && (
        <div className="space-y-4">
          {/* Compact quick-type composer */}
          <div className="flex items-center gap-2">
            <NoteAvatar name={userProfile.name} photo={userProfile.photo} />
            <input
              type="text"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !postingNote && noteDraft.trim()) postNote();
              }}
              placeholder="Post a note…"
              className="flex-1 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-red-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <button
              type="button"
              disabled={postingNote || !noteDraft.trim()}
              onClick={postNote}
              title="Post note"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-600 text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>

          {/* Separator between the composer and the notes feed */}
          <div className="border-b border-gray-200 dark:border-gray-700" />

          {noteMessages.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No notes yet. Notes posted here are internal-only.
            </p>
          ) : (
            <div className="space-y-3">
              {noteMessages.map((n, i) => {
                const author = (n?.byUid && noteAuthors[n.byUid]) || null;
                const authorName = author?.name || n?.byName || 'Internal user';
                const authorPhoto = author?.photo || n?.byPhoto || '';
                return (
                  <div
                    key={`${noteTimeMs(n?.at)}-${i}`}
                    className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                  >
                    <NoteAvatar name={authorName} photo={authorPhoto} />
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{authorName}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 dark:text-gray-500">{formatNoteTime(n?.at)}</span>
                          {n?.byUid && n.byUid === currentUser?.uid && (
                            <button
                              type="button"
                              onClick={() => deleteNote(n)}
                              title="Delete note"
                              className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-500 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{n?.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {(item.internalOnly?.notes || '').trim() && (
            <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900/50 dark:bg-amber-900/10">
              <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Legacy internal notes</h3>
              <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{item.internalOnly.notes}</p>
            </section>
          )}
        </div>
        )}

        {activeTab === 'financial' && showFinancialTab && (
            <div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {/* Price — editable */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Price</p>
                  {editing ? (
                    <div className="mt-2 flex items-center gap-1">
                      <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          catalogDraft?.clientListPrice !== '' &&
                          catalogDraft?.clientListPrice !== undefined &&
                          catalogDraft?.clientListPrice !== null
                            ? catalogDraft.clientListPrice
                            : catalogDraft?.standardPricing?.cost ?? ''
                        }
                        onChange={(e) => setCatalogDraft((prev) => ({ ...prev, clientListPrice: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-2xl font-bold text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  ) : (
                    <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                      {formatMoney(pricingSummary.clientNum)}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">What the client pays</p>
                </div>

                {/* Cost — editable */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Cost</p>
                  {editing ? (
                    <div className="mt-2 flex items-center gap-1">
                      <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={catalogDraft?.internalCost ?? ''}
                        onChange={(e) => setCatalogDraft((prev) => ({ ...prev, internalCost: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-2xl font-bold text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  ) : (
                    <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                      {formatMoney(pricingSummary.internalNum)}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">Internal cost to deliver</p>
                </div>

                {/* Profit — computed */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Profit</p>
                  <p
                    className={`mt-2 text-3xl font-bold ${
                      Number.isFinite(pricingSummary.profit)
                        ? pricingSummary.profit >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {formatMoney(pricingSummary.profit)}
                  </p>
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">Price − cost</p>
                </div>

                {/* Margin — computed */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Margin</p>
                  <p
                    className={`mt-2 text-3xl font-bold ${
                      pricingSummary.marginPercent != null && Number.isFinite(pricingSummary.marginPercent)
                        ? pricingSummary.marginPercent >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {pricingSummary.marginPercent != null && Number.isFinite(pricingSummary.marginPercent)
                      ? `${pricingSummary.marginPercent.toFixed(1)}%`
                      : '—'}
                  </p>
                  <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">Profit ÷ price</p>
                </div>
              </div>
              <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
                {editing
                  ? 'Enter price and cost — profit and margin update as you type. Save to keep changes.'
                  : 'Hit Edit to change price and cost. Price falls back to the standard cost when no client list price is set.'}
              </p>
            </div>
        )}
      </div>

      {/* Icon picker (edit mode) */}
      <Modal isOpen={showEmojiPicker} onClose={() => setShowEmojiPicker(false)} maxWidth="xl" zIndex={100}>
        <h2 className="mb-1 text-center text-xl font-semibold text-gray-900 dark:text-white">Choose an icon</h2>
        <p className="mb-4 text-center text-sm text-gray-500 dark:text-gray-400">
          {catalogDraft?.name || 'This item'}
        </p>
        <div className="flex justify-center">
          {showEmojiPicker && (
            <React.Suspense
              fallback={
                <div className="flex h-72 w-full items-center justify-center">
                  <BBALoader size={32} />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => {
                  setCatalogDraft((prev) => ({ ...prev, icon: emojiData.emoji }));
                  setShowEmojiPicker(false);
                }}
                theme={darkMode ? 'dark' : 'light'}
                width="100%"
                height={380}
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </React.Suspense>
          )}
        </div>
        <div className="mt-4 flex items-center justify-between">
          {catalogDraft?.icon && catalogDraft.icon !== '📦' ? (
            <button
              onClick={() => {
                setCatalogDraft((prev) => ({ ...prev, icon: '📦' }));
                setShowEmojiPicker(false);
              }}
              className="rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Use default icon
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => setShowEmojiPicker(false)}
            className={`rounded-md px-4 py-2 text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
}
